import { fetchJSON as fetchAPI } from './client';

export type SecurityDashboard = {
  tenant_id: string;
  open_incidents: number;
  critical_incidents: number;
  active_threats: number;
  blocked_events: number;
  average_risk_score: number;
  compliance_score: number;
  active_sessions: number;
  trusted_devices: number;
  pending_reviews: number;
  secrets_expiring: number;
  security_health: string;
  zero_trust_enabled: boolean;
  generated_at: string;
  platform_health?: { module: string; status: string; latency_ms?: number }[];
  threat_trends?: { category: string; count: number }[];
};

export type SecurityIncident = {
  id: string;
  tenant_id: string;
  title: string;
  description?: string;
  category: string;
  severity: string;
  status: string;
  risk_score: number;
  detected_at: string;
};

export type SecurityEvent = {
  id: string;
  tenant_id: string;
  category: string;
  source: string;
  actor?: string;
  resource?: string;
  action: string;
  risk_score: number;
  blocked: boolean;
  occurred_at: string;
};

export type RiskScore = {
  id: string;
  tenant_id: string;
  entity_type: string;
  entity_id: string;
  score: number;
  level: string;
  factors?: string[];
  updated_at: string;
};

export type ComplianceDashboard = {
  tenant_id: string;
  assessments: { framework: string; score: number; passed_controls: number; total_controls: number; status: string }[];
  controls: { control_id: string; name: string; framework: string; status: string }[];
  overall_score: number;
  status: string;
  generated_at: string;
};

export type AuditRecord = {
  id: string;
  tenant_id: string;
  actor: string;
  action: string;
  resource: string;
  outcome: string;
  occurred_at: string;
};

export type SecurityPolicy = {
  id: string;
  name: string;
  type: string;
  resource?: string;
  effect: string;
  conditions?: string[];
  active: boolean;
};

export type Secret = {
  id: string;
  name: string;
  type: string;
  status: string;
  rotated_at?: string;
  expires_at?: string;
};

export type SOCDashboard = {
  tenant_id: string;
  incidents: SecurityIncident[];
  recent_events: SecurityEvent[];
  threat_rules: { id: string; name: string; category: string; enabled: boolean }[];
  risk_scores: RiskScore[];
  security_timeline: { id: string; action: string; message: string; created_at: string }[];
  generated_at: string;
};

const ROOT = import.meta.env.VITE_SECURITY_API ?? '/api/v1';

function fetchRoot<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT, path, init);
}

export const securityApi = {
  dashboard: () => fetchRoot<SecurityDashboard>('/security/dashboard'),
  soc: () => fetchRoot<SOCDashboard>('/security/soc'),
  incidents: () => fetchRoot<SecurityIncident[]>('/security/incidents'),
  threats: () => fetchRoot<SecurityEvent[]>('/security/threats'),
  detectThreat: (body: { category: string; source: string; actor?: string; resource?: string; action?: string }) =>
    fetchRoot<SecurityEvent>('/security/threats', { method: 'POST', body: JSON.stringify(body) }),
  risks: () => fetchRoot<RiskScore[]>('/security/risks'),
  compliance: () => fetchRoot<ComplianceDashboard>('/security/compliance'),
  audit: () => fetchRoot<AuditRecord[]>('/security/audit'),
  createAccessReview: (body: { subject_type: string; subject_id: string; reviewer?: string }) =>
    fetchRoot<{ id: string; status: string }>('/security/access-review', { method: 'POST', body: JSON.stringify(body) }),
  rotateKey: (body: { secret_id: string; initiated_by?: string }) =>
    fetchRoot<{ id: string; status: string }>('/security/key-rotation', { method: 'POST', body: JSON.stringify(body) }),
  policies: () => fetchRoot<SecurityPolicy[]>('/security/policies'),
  secrets: () => fetchRoot<Secret[]>('/security/secrets'),
  activity: () => fetchRoot<{ id: string; action: string; message: string; created_at: string }[]>('/security/activity'),
};
